import { useState, useEffect, useCallback } from 'react'
import ManagerMobileLayout from '../../components/ManagerMobileLayout'
import api from '../../lib/api'

interface PresenceEntry {
  id: string
  firstName: string
  lastName: string
  establishmentId: string
  lastEvent: string | null
  lastEventAt: string | null
  lateMinutes: number | null
  adjustedTime: string | null
  isPresent: boolean
  plannedStart: string | null
  plannedEnd: string | null
}

type FilterType = 'all' | 'present' | 'shift' | 'late'

const STATUS_CONFIG: Record<string, { label: string; dot: string; badge: string }> = {
  arrive:  { label: 'Présent',    dot: 'bg-green-400',  badge: 'bg-green-100 text-green-700' },
  resume:  { label: 'Présent',    dot: 'bg-green-400',  badge: 'bg-green-100 text-green-700' },
  break:   { label: 'En pause',   dot: 'bg-yellow-400', badge: 'bg-yellow-100 text-yellow-700' },
  leave:   { label: 'Parti',      dot: 'bg-gray-300',   badge: 'bg-gray-100 text-gray-500' },
}

function getStatus(entry: PresenceEntry) {
  if (!entry.lastEvent) return null
  return STATUS_CONFIG[entry.lastEvent] || null
}

function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
}

function isLateArrival(entry: PresenceEntry): boolean {
  return !!(entry.lateMinutes && entry.lateMinutes > 0 && entry.lastEvent === 'arrive')
}

export default function ManagerBadges() {
  const [presence, setPresence] = useState<PresenceEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<FilterType>('all')
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date())

  const load = useCallback(async () => {
    try {
      const r = await api.get('/clock/live')
      setPresence(r.data)
      setLastRefresh(new Date())
    } catch { /* ignore */ }
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
    const interval = setInterval(load, 60_000)
    return () => clearInterval(interval)
  }, [load])

  const filtered = presence.filter(e => {
    if (filter === 'present') return e.isPresent
    if (filter === 'shift') return !!e.plannedStart
    if (filter === 'late') return isLateArrival(e)
    return true
  }).sort((a, b) => {
    // Sort: present first, then by planned shift start, then alphabetically
    const aPresent = a.isPresent ? 0 : a.lastEvent === 'break' ? 1 : a.lastEvent === 'leave' ? 3 : 2
    const bPresent = b.isPresent ? 0 : b.lastEvent === 'break' ? 1 : b.lastEvent === 'leave' ? 3 : 2
    if (aPresent !== bPresent) return aPresent - bPresent
    if (a.plannedStart && b.plannedStart) return a.plannedStart.localeCompare(b.plannedStart)
    if (a.plannedStart) return -1
    if (b.plannedStart) return 1
    return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
  })

  const presentCount = presence.filter(e => e.isPresent).length
  const breakCount = presence.filter(e => e.lastEvent === 'break').length
  const shiftCount = presence.filter(e => e.plannedStart).length
  const lateCount = presence.filter(isLateArrival).length

  const FILTERS: { key: FilterType; label: string; count?: number }[] = [
    { key: 'all',     label: 'Tous',     count: presence.length },
    { key: 'present', label: 'Présents', count: presentCount },
    { key: 'shift',   label: 'Shifts',   count: shiftCount },
    { key: 'late',    label: 'Retards',  count: lateCount },
  ]

  return (
    <ManagerMobileLayout>
      <div className="p-4 space-y-4">

        {/* Summary card */}
        <div className="bg-indigo-700 rounded-2xl p-4 text-white">
          <div className="flex items-center justify-between mb-2">
            <div className="text-indigo-200 text-sm">Présences du jour</div>
            <button onClick={load} className="text-indigo-200 text-xs hover:text-white active:scale-95 transition-transform">
              Actualiser
            </button>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white/10 rounded-xl p-2.5 text-center">
              <div className="text-2xl font-bold">{presentCount}</div>
              <div className="text-indigo-200 text-xs mt-0.5">Présents</div>
            </div>
            <div className="bg-white/10 rounded-xl p-2.5 text-center">
              <div className="text-2xl font-bold">{breakCount}</div>
              <div className="text-indigo-200 text-xs mt-0.5">En pause</div>
            </div>
            <div className="bg-white/10 rounded-xl p-2.5 text-center">
              <div className={`text-2xl font-bold ${lateCount > 0 ? 'text-amber-300' : ''}`}>{lateCount}</div>
              <div className="text-indigo-200 text-xs mt-0.5">Retards</div>
            </div>
          </div>
          <div className="text-indigo-300 text-xs mt-2 text-right">
            Mis à jour {lastRefresh.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                filter === f.key
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-600 border border-gray-200'
              }`}
            >
              {f.label}
              {f.count !== undefined && (
                <span className={`ml-1.5 ${filter === f.key ? 'text-indigo-200' : 'text-gray-400'}`}>
                  {f.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Employee list */}
        {loading ? (
          <div className="text-center py-12 text-gray-400">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm text-gray-400">
            Aucun employé trouvé
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(entry => {
              const status = getStatus(entry)
              const late = isLateArrival(entry)
              const noShow = !entry.lastEvent && entry.plannedStart

              return (
                <div
                  key={entry.id}
                  className={`bg-white rounded-2xl shadow-sm p-4 flex items-center gap-3 ${late ? 'border-l-4 border-amber-400' : noShow ? 'border-l-4 border-red-300' : ''}`}
                >
                  {/* Presence dot */}
                  <div className="relative flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-blue-500 flex items-center justify-center text-white font-bold text-sm">
                      {entry.firstName[0]}
                    </div>
                    {status && (
                      <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${status.dot}`} />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-800 text-sm">
                      {entry.firstName} {entry.lastName !== '-' ? entry.lastName : ''}
                    </div>

                    {/* Planned shift */}
                    {entry.plannedStart ? (
                      <div className="text-xs text-gray-500 mt-0.5">
                        Shift {entry.plannedStart} – {entry.plannedEnd}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-400 italic mt-0.5">Pas de shift aujourd'hui</div>
                    )}

                    {/* Clock-in time */}
                    {entry.lastEventAt && (
                      <div className="text-xs mt-0.5">
                        {status?.label} depuis {fmtTime(entry.lastEventAt)}
                        {entry.adjustedTime && entry.adjustedTime !== fmtTime(entry.lastEventAt) && (
                          <span className="text-blue-500 ml-1">→ {entry.adjustedTime}</span>
                        )}
                      </div>
                    )}

                    {/* Late warning */}
                    {late && entry.lateMinutes && (
                      <div className="text-xs text-amber-600 font-medium mt-0.5">
                        Retard de {entry.lateMinutes}min
                      </div>
                    )}

                    {/* No show warning */}
                    {noShow && (
                      <div className="text-xs text-red-500 font-medium mt-0.5">
                        Non pointer — shift a {entry.plannedStart}
                      </div>
                    )}
                  </div>

                  {/* Status badge */}
                  <div className="flex-shrink-0">
                    {status ? (
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${status.badge}`}>
                        {status.label}
                      </span>
                    ) : entry.plannedStart ? (
                      <span className="text-xs px-2 py-1 rounded-full font-medium bg-red-100 text-red-600">
                        Absent
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-1 rounded-full font-medium bg-gray-100 text-gray-400">
                        Repos
                      </span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </ManagerMobileLayout>
  )
}
